import { FunctionComponent, useEffect, useRef, useState } from "react";
import "./VideoTutorialCategory.scss";
import Table from "../../Components/Table/Table";
import CustomToolbar from "../../Components/Table/components/CustomToolbar";
import ButtonField from "../../Components/Button/ButtonField";
import { Columns } from "../../Components/Table/ITable";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { IVideoTutorialCategory } from "./IVideoTutorialCategory";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import Dialog from "../../Components/Dialog/Dialog";
import VideoTutorialCategoryForm from "./VideoTutorialCategoryForm";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";
import TableSearch from "../../Components/Table/components/TableSearch";
import { ReactComponent as Delete } from "../../Assets/Images/delete.svg";
import { ReactComponent as Edit } from "../../Assets/Images/edit.svg";
import { IDeleteDialog } from "../../Components/DeleteDialog/IDeleteDialog";
import IconButtonField from "../../Components/Button/IconButtonField";
import { SvgIcon } from "@mui/material";
import DeleteDialog from "../../Components/DeleteDialog/DeleteDialog";
import { ReactComponent as RefreshButton } from "../../Assets/Images/RefreshButton.svg";
import { listVideoTutorialCategory, clearListVideoTutorialCategory } from "../../Redux/Slices/VideoTutorialCategory/ListVideoTutorialCategorySlice";
import { clearAddVideoTutorialCategoryResponse } from "../../Redux/Slices/VideoTutorialCategory/AddVideoTutorialCategorySlice";
import { clearEditVideoTutorialCategoryResponse } from "../../Redux/Slices/VideoTutorialCategory/EditVideoTutorialCategorySlice";
import { deleteVideoTutorialCategory, clearDeleteVideoTutorialCategoryResponse } from "../../Redux/Slices/VideoTutorialCategory/DeleteVideoTutorialCategorySlice";

interface VideoTutorialCategoryProps { }

const VideoTutorialCategory: FunctionComponent<VideoTutorialCategoryProps> = () => {
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [deleteDialogData, setDeleteData] = useState<IDeleteDialog>({});
    const [initialValues, setInitialValues] = useState<Partial<IVideoTutorialCategory>>({
        status: true,
    });

    const dispatch = useAppDispatch();
    const { data: listCategories } = useAppSelector((state) => state.ListVideoTutorialCategorySlice);
    const { data: addResponse } = useAppSelector((state) => state.AddVideoTutorialCategorySlice);
    const { data: editResponse } = useAppSelector((state) => state.EditVideoTutorialCategorySlice);
    const { data: deleteResponse } = useAppSelector((state) => state.DeleteVideoTutorialCategorySlice);

    const permissions = useAppSelector((state) => state.PermissionsToPage);
    const finaldata = { permissions };

    const formRef = useRef<HTMLFormElement>(null);

    const columns: Columns = [
        {
            field: "index",
            renderHeader: () => <b>S.No</b>,
            width: 90,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "meta_value",
            renderHeader: () => <b>Category Name</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
        },
        {
            field: "status",
            renderHeader: () => <b>Status</b>,
            flex: 1,
            type: "string",
            headerAlign: "left",
            cellClassName: (params) =>
                `activename ${params.row.status ? "active" : "inactive"}`,
            valueFormatter: (params) => (params.value === true ? "Active" : "Inactive"),
        },
        {
            field: "action",
            renderHeader: () => <b>Action</b>,
            flex: 1,
            type: "actions",
            renderCell: (params: GridRenderCellParams<IVideoTutorialCategory>) => {
                return (
                    <>
                        <IconButtonField
                            disabled={finaldata?.permissions?.edit === false}
                            onClick={() => {
                                setInitialValues({ ...params.row });
                                setIsEdit(true);
                                setDialogOpen(true);
                            }}
                        >
                            <SvgIcon sx={{ fill: "none" }} component={Edit} inheritViewBox />
                        </IconButtonField>
                        <IconButtonField
                            disabled={finaldata?.permissions?.delete === false}
                            onClick={() => {
                                setDeleteData({ id: params.id, name: params?.row?.meta_value });
                                setDeleteDialogOpen(true);
                            }}
                        >
                            <SvgIcon component={Delete} inheritViewBox />
                        </IconButtonField>
                    </>
                );
            },
        },
    ];

    const rawData = Array.isArray(listCategories) ? listCategories : (listCategories as any)?.data;
    const categoriesData = Array.isArray(rawData) ? rawData : [];
    const rows = categoriesData.map((cat, index) => ({
        ...cat,
        id: cat.id,
        index: index + 1
    }));

    useEffect(() => {
        dispatch(listVideoTutorialCategory({}));
    }, [addResponse, editResponse, deleteResponse, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(clearAddVideoTutorialCategoryResponse());
            dispatch(clearEditVideoTutorialCategoryResponse());
            dispatch(clearDeleteVideoTutorialCategoryResponse());
            dispatch(clearListVideoTutorialCategory());
        };
    }, [dispatch]);

    return (
        <>
            <Table rows={rows} columns={columns} exportFileName="VideoTutorialCategories">
                <CustomToolbar>
                    <h6></h6>
                    <TableSearch />
                    <ButtonField
                        className="tabel_add_new_button"
                        onClick={() => {
                            setInitialValues({ status: true });
                            setIsEdit(false);
                            setDialogOpen(true);
                        }}
                        toolTip="Add new"
                        disabled={finaldata?.permissions?.add === false}
                    >
                        Add new
                    </ButtonField>
                    <IconButtonField
                        className="refresh_btn"
                        toolTip="Refresh"
                        onClick={() => dispatch(listVideoTutorialCategory({}))}
                    >
                        <SvgIcon
                            className="refresh_svg_icon"
                            component={RefreshButton}
                            inheritViewBox
                        />
                    </IconButtonField>
                </CustomToolbar>
            </Table>

            <Dialog
                open={dialogOpen}
                dialogClassName="dialog"
                Dialog_Title={`${isEdit ? "Update" : "Add"} Video Tutorial Category`}
                dialogContentClassName="dialog_content"
                Dialog_Content={
                    <VideoTutorialCategoryForm
                        formRef={formRef}
                        initialValues={initialValues}
                        setInitialValues={setInitialValues}
                    />
                }
                Dialog_Actions={
                    <>
                        <ButtonField
                            className="form_cancel_button"
                            onClick={() => {
                                setDialogOpen(false);
                                setIsEdit(false);
                            }}
                        >
                            Cancel
                        </ButtonField>
                        <ButtonField
                            className="form_save_button"
                            onClick={() => formRef.current?.requestSubmit()}
                        >
                            Save
                        </ButtonField>
                    </>
                }
            />
            <ResponseDialog response={addResponse} parentDialog={setDialogOpen} />
            <ResponseDialog response={editResponse} parentDialog={setDialogOpen} />
            <DeleteDialog
                deleteableName={deleteDialogData.name ?? ""}
                open={deleteDialogOpen}
                setOpen={setDeleteDialogOpen}
                deleteFunction={() => {
                    dispatch(deleteVideoTutorialCategory({ id: deleteDialogData.id, is_delete: true }));
                }}
                response={deleteResponse}
            />
        </>
    );
};

export default VideoTutorialCategory;